import type { QuoteRequestBody, ScenarioCatalogItem } from "@ti/core";

/** Limits mirror QuoteRequestBody (packages/core/src/schemas.ts). */
export const LIMITS = {
  subLimit: { min: 1_000, max: 50_000_000 },
  aggregate: { min: 10_000, max: 100_000_000 },
  perClaim: { min: 1_000, max: 50_000_000 },
  deductible: { min: 0, max: 1_000_000 },
  name: { min: 2, max: 120 },
} as const;

export const STEPS = [
  { id: "agents", label: "Agents", hint: "Name and agents" },
  { id: "scenarios", label: "Scenarios", hint: "What to insure" },
  { id: "terms", label: "Limits & terms", hint: "Aggregate, per claim, deductible" },
  { id: "review", label: "Review & bind", hint: "Premium and bind" },
] as const;
export type StepId = (typeof STEPS)[number]["id"];

export const lineKey = (agentId: string, key: string) => `${agentId}:${key}`;

export interface Selection {
  on: boolean;
  subLimit: string;
}

export interface QuoteForm {
  name: string;
  selection: Record<string, Selection>;
  /** null = follow the suggestion derived from the selected sub-limits. */
  aggregate: string | null;
  perClaim: string | null;
  deductible: string;
  mode: "graduated" | "strict";
}

export const EMPTY_FORM: QuoteForm = { name: "", selection: {}, aggregate: null, perClaim: null, deductible: "1,000", mode: "graduated" };

/** Parses "$25,000" / "25000" / "25k" into a whole number of dollars (NaN when unparseable). */
export function parseMoney(v: string): number {
  const s = v.trim().toLowerCase().replace(/[$,\s]/g, "");
  if (!s) return Number.NaN;
  const m = /^(\d+(?:\.\d+)?)([km]?)$/.exec(s);
  if (!m) return Number.NaN;
  const n = Number(m[1]) * (m[2] === "k" ? 1_000 : m[2] === "m" ? 1_000_000 : 1);
  return Math.round(n);
}

export const isAvailable = (c: ScenarioCatalogItem) => c.eligible && !c.coveredByPolicyId;

const roundUp = (n: number, step: number) => Math.ceil(n / step) * step;

export function suggestAggregate(subLimits: number[]) {
  const sum = subLimits.reduce((a, b) => a + b, 0);
  return Math.max(LIMITS.aggregate.min, roundUp(sum, 10_000));
}

export function suggestPerClaim(subLimits: number[]) {
  const max = Math.max(0, ...subLimits);
  return Math.max(LIMITS.perClaim.min, Math.min(25_000, roundUp(max, 1_000)));
}

export interface Derived {
  agentIds: string[];
  lines: Array<{ item: ScenarioCatalogItem; subLimit: number; raw: string }>;
  aggregate: number;
  perClaim: number;
  deductible: number;
  aggregateRaw: string;
  perClaimRaw: string;
  estimate: number;
}

export function derive(form: QuoteForm, agentIds: string[], catalog: ScenarioCatalogItem[] | undefined): Derived {
  const lines = (catalog ?? [])
    .filter((c) => agentIds.includes(c.agentId) && isAvailable(c) && form.selection[lineKey(c.agentId, c.key)]?.on)
    .map((item) => {
      const raw = form.selection[lineKey(item.agentId, item.key)]?.subLimit ?? "";
      return { item, raw, subLimit: parseMoney(raw) };
    });
  const valid = lines.map((l) => l.subLimit).filter((n) => Number.isFinite(n));
  const aggregateRaw = form.aggregate ?? suggestAggregate(valid).toLocaleString("en-US");
  const perClaimRaw = form.perClaim ?? suggestPerClaim(valid).toLocaleString("en-US");
  const estimate = lines.reduce((n, l) => n + (Number.isFinite(l.subLimit) ? (l.item.ratePer1k * l.subLimit) / 1000 : 0), 0);
  return {
    agentIds,
    lines,
    aggregateRaw,
    perClaimRaw,
    aggregate: parseMoney(aggregateRaw),
    perClaim: parseMoney(perClaimRaw),
    deductible: parseMoney(form.deductible || "0"),
    estimate,
  };
}

export type Errors = Partial<Record<"name" | "agents" | "scenarios" | "aggregate" | "perClaim" | "deductible", string>> & { lines?: Record<string, string> };

const usd = (n: number) => `$${n.toLocaleString("en-US")}`;

function checkRange(n: number, { min, max }: { min: number; max: number }, what: string) {
  if (!Number.isFinite(n)) return `Enter ${what} as a whole dollar amount.`;
  if (n < min) return `${what[0]!.toUpperCase()}${what.slice(1)} must be at least ${usd(min)}.`;
  if (n > max) return `${what[0]!.toUpperCase()}${what.slice(1)} can't exceed ${usd(max)}.`;
  return undefined;
}

export function validate(form: QuoteForm, d: Derived): Errors {
  const e: Errors = {};
  const name = form.name.trim();
  if (name.length < LIMITS.name.min) e.name = "Give the policy a name of at least 2 characters.";
  else if (name.length > LIMITS.name.max) e.name = "Keep the name under 120 characters.";
  if (!d.agentIds.length) e.agents = "Select at least one agent to insure.";

  if (!d.lines.length) e.scenarios = "Select at least one eligible scenario.";
  const lineErrors: Record<string, string> = {};
  for (const l of d.lines) {
    const err = checkRange(l.subLimit, LIMITS.subLimit, "the sub-limit");
    if (err) lineErrors[lineKey(l.item.agentId, l.item.key)] = err;
  }
  if (Object.keys(lineErrors).length) {
    e.lines = lineErrors;
    e.scenarios ??= "Fix the highlighted sub-limits.";
  }

  const maxSub = Math.max(0, ...d.lines.map((l) => l.subLimit).filter(Number.isFinite));
  e.aggregate = checkRange(d.aggregate, LIMITS.aggregate, "the aggregate limit");
  if (!e.aggregate && d.aggregate < maxSub) e.aggregate = `The aggregate limit must be at least your largest sub-limit (${usd(maxSub)}).`;
  e.perClaim = checkRange(d.perClaim, LIMITS.perClaim, "the per-claim limit");
  if (!e.perClaim && Number.isFinite(d.aggregate) && d.perClaim > d.aggregate) e.perClaim = "The per-claim limit can't exceed the aggregate limit.";
  e.deductible = checkRange(d.deductible, LIMITS.deductible, "the deductible");
  if (!e.deductible && Number.isFinite(d.perClaim) && d.deductible >= d.perClaim) e.deductible = "The deductible must be lower than the per-claim limit.";

  for (const k of Object.keys(e) as Array<keyof Errors>) if (e[k] === undefined) delete e[k];
  return e;
}

/** First step whose inputs are invalid (the furthest step the user may open). */
export function firstInvalidStep(e: Errors): StepId | null {
  if (e.name || e.agents) return "agents";
  if (e.scenarios || e.lines) return "scenarios";
  if (e.aggregate || e.perClaim || e.deductible) return "terms";
  return null;
}

export function toRequest(form: QuoteForm, d: Derived): QuoteRequestBody {
  return {
    name: form.name.trim(),
    agentIds: [...new Set(d.lines.map((l) => l.item.agentId))],
    scenarios: d.lines.map((l) => ({ agentId: l.item.agentId, key: l.item.key, subLimit: l.subLimit })),
    aggregateLimit: d.aggregate,
    perClaimLimit: d.perClaim,
    deductible: d.deductible,
    mode: form.mode,
    termMonths: 12,
  };
}

export function parseAgentsParam(v: string | undefined) {
  return (v ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
